import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AdminCompanyContext } from '../../AdminApp';
import { getAdminFeedback } from '../../adminApi';
import { useCompanyNames, resolveCompanyName } from '../../hooks/useCompanyNames';
import { useUserNames, resolveUserName } from '../../hooks/useUserNames';

const PAGE_SIZE = 50;
const PERIODS = [
  { label: '7d',       days: 7    },
  { label: '30d',      days: 30   },
  { label: 'All time', days: null },
];
const RATINGS = [
  { label: 'All',   value: ''     },
  { label: '👍',    value: 'up'   },
  { label: '👎',    value: 'down' },
];

function sinceFromDays(days) {
  if (!days) return undefined;
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function shortId(id) {
  if (!id) return '—';
  return id.length > 14 ? id.slice(0, 6) + '…' + id.slice(-4) : id;
}

function truncate(str, n) {
  if (!str) return '—';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

function exportCsv(rows) {
  const headers = ['Time','Company','User','Question','Answer','Rating','Comment','Eval Score','Model','Tool Calls','DSL Success','DSL'];
  const lines = rows.map(r => [
    r.submitted_at,
    r.company_id,
    r.user_id,
    `"${(r.question || '').replace(/"/g, '""')}"`,
    `"${(r.answer   || '').replace(/"/g, '""')}"`,
    r.rating,
    `"${(r.comment  || '').replace(/"/g, '""')}"`,
    r.evaluator_score ?? '',
    r.model_flavor ?? '',
    r.tool_call_count ?? '',
    r.dsl_success ?? '',
    `"${(r.dsl || '').replace(/"/g, '""')}"`,
  ].join(','));
  const csv  = [headers.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `feedback-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function RatingBadge({ rating }) {
  if (rating === 'up')   return <span className="admin-rating-up">👍</span>;
  if (rating === 'down') return <span className="admin-rating-down">👎</span>;
  return <span>—</span>;
}

function ExpandedRow({ row, colSpan }) {
  return (
    <tr className="admin-feedback-expanded">
      <td colSpan={colSpan}>
        <div className="admin-feedback-detail">
          <div className="admin-feedback-detail-block">
            <div className="admin-feedback-detail-label">Question</div>
            <div className="admin-feedback-detail-text">{row.question || '—'}</div>
          </div>
          <div className="admin-feedback-detail-block">
            <div className="admin-feedback-detail-label">Answer</div>
            <div className="admin-feedback-detail-text">{row.answer || '—'}</div>
          </div>
          {row.dsl && (
            <div className="admin-feedback-detail-block">
              <div className="admin-feedback-detail-label">DSL Query</div>
              <pre className="admin-feedback-detail-pre">{
                typeof row.dsl === 'string' ? row.dsl : JSON.stringify(row.dsl, null, 2)
              }</pre>
            </div>
          )}
          <div className="admin-feedback-detail-meta">
            <span><strong>Model:</strong> {row.model_flavor || '—'}</span>
            <span><strong>Tool calls:</strong> {row.tool_call_count ?? '—'}</span>
            <span><strong>Eval score:</strong> {row.evaluator_score ?? '—'}</span>
            <span><strong>DSL success:</strong> {row.dsl_success == null ? '—' : row.dsl_success ? 'yes' : 'no'}</span>
            <span><strong>Retries:</strong> {row.retries_used ?? '—'}</span>
            <span><strong>Session:</strong> <span title={row.session_id}>{shortId(row.session_id)}</span></span>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function AdminFeedback() {
  const { selectedCompany } = useContext(AdminCompanyContext);
  const companyNames = useCompanyNames();
  const userNames = useUserNames();

  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [period, setPeriod]     = useState(1);
  const [rating, setRating]     = useState('');
  const [company, setCompany]   = useState('');
  const [page, setPage]         = useState(0);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (selectedCompany) setCompany(selectedCompany);
  }, [selectedCompany]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const since = sinceFromDays(PERIODS[period].days);
      const data  = await getAdminFeedback({
        company_id: company || undefined,
        rating:     rating  || undefined,
        since,
        limit: 200,
      });
      setItems(data.items || []);
      setPage(0);
      setExpanded(null);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [period, rating, company]);

  useEffect(() => { load(); }, [load]);

  // ── Stats ────────────────────────────────────────────────────────────────────
  const upCount   = items.filter(r => r.rating === 'up').length;
  const downCount = items.filter(r => r.rating === 'down').length;
  const scores    = items.map(r => r.evaluator_score).filter(s => s != null);
  const avgScore  = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : '—';

  // ── Pagination ───────────────────────────────────────────────────────────────
  const pageCount = Math.ceil(items.length / PAGE_SIZE);
  const pageItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const COLS = 7;

  return (
    <div>
      {/* ── Header + filters ── */}
      <div className="admin-toolbar">
        <h2 className="admin-section-title" style={{ margin: 0 }}>Feedback</h2>
        <div className="admin-toolbar-controls">
          <div className="admin-period-btns">
            {PERIODS.map((p, i) => (
              <button key={p.label} className={`admin-period-btn${period === i ? ' active' : ''}`} onClick={() => setPeriod(i)}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="admin-period-btns">
            {RATINGS.map(r => (
              <button key={r.value} className={`admin-period-btn${rating === r.value ? ' active' : ''}`} onClick={() => setRating(r.value)}>
                {r.label}
              </button>
            ))}
          </div>
          <input
            className="admin-company-input"
            placeholder="Filter by company ID…"
            value={company}
            onChange={e => setCompany(e.target.value)}
          />
          <button className="admin-export-btn" onClick={() => exportCsv(items)} disabled={items.length === 0}>
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-error-state">
          <p>{error}</p>
          <button onClick={() => { setLoading(true); load(); }}>Retry</button>
        </div>
      )}

      {/* ── Stats bar ── */}
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        {loading ? (
          [1,2,3,4].map(i => (
            <div key={i} className="stat-card">
              <div className="admin-skeleton admin-skeleton-label" />
              <div className="admin-skeleton admin-skeleton-value" />
            </div>
          ))
        ) : (
          <>
            <div className="stat-card">
              <div className="stat-card-label">Total</div>
              <div className="stat-card-value">{items.length.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Thumbs Up</div>
              <div className="stat-card-value">
                {upCount}
                {items.length > 0 && <span className="stat-card-sub"> ({((upCount / items.length) * 100).toFixed(0)}%)</span>}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Thumbs Down</div>
              <div className="stat-card-value">
                {downCount}
                {items.length > 0 && <span className="stat-card-sub"> ({((downCount / items.length) * 100).toFixed(0)}%)</span>}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Avg Eval Score</div>
              <div className="stat-card-value">{avgScore}</div>
            </div>
          </>
        )}
      </div>

      {/* ── Table ── */}
      {!loading && !error && (
        <>
          <div className="admin-table-meta">
            {items.length} record{items.length !== 1 ? 's' : ''} — click a row to expand
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Company</th>
                  <th>User</th>
                  <th>Question</th>
                  <th>Rating</th>
                  <th>Eval Score</th>
                  <th>Comment</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length === 0 ? (
                  <tr><td colSpan={COLS} style={{ textAlign: 'center', color: 'var(--admin-text-muted)' }}>No feedback found.</td></tr>
                ) : pageItems.map(r => (
                  <React.Fragment key={r.message_id}>
                    <tr
                      onClick={() => setExpanded(expanded === r.message_id ? null : r.message_id)}
                      className={expanded === r.message_id ? 'admin-row-selected' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.submitted_at)}</td>
                      <td title={r.company_id}>{resolveCompanyName(companyNames, r.company_id)}</td>
                      <td title={r.user_id}>{resolveUserName(userNames, r.user_id)}</td>
                      <td title={r.question} style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {truncate(r.question, 80)}
                      </td>
                      <td><RatingBadge rating={r.rating} /></td>
                      <td style={{ textAlign: 'right' }}>{r.evaluator_score ?? '—'}</td>
                      <td style={{ color: 'var(--admin-text-muted)', fontStyle: r.comment ? 'normal' : 'italic' }}>
                        {truncate(r.comment, 60) || 'no comment'}
                      </td>
                    </tr>
                    {expanded === r.message_id && <ExpandedRow row={r} colSpan={COLS} />}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="admin-pagination">
              <button onClick={() => setPage(p => p - 1)} disabled={page === 0}>← Prev</button>
              <span>Page {page + 1} of {pageCount}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= pageCount - 1}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
